import { useEffect, useState, useMemo } from "react";
import { api } from "../api";
import { fmtMonth } from "../format";
import type { MacroScope, Metrics } from "../types";

const money = new Intl.NumberFormat(undefined, { style: "currency", currency: "USD", notation: "compact", maximumFractionDigits: 1 });
const moneyDetail = new Intl.NumberFormat(undefined, { style: "currency", currency: "USD", notation: "compact", maximumFractionDigits: 2 });
const pct = (v: number | null, signed = false) =>
  v == null ? "—" : `${signed && v > 0 ? "+" : v < 0 ? "−" : ""}${Math.abs(v).toFixed(1)}%`;
const monthRange = (ms: string[]) => (ms.length ? `${fmtMonth(ms[0])}–${fmtMonth(ms[ms.length - 1])}` : "");

// Placeholder values while the tenant has no source for a metric (shipments, past Statistical
// forecast, feasible supply). Shown marked as samples so nobody mistakes them for Kinaxis data.
const SAMPLE = { fill_rate: 93.4, wape: 21.7, bias: 4.2, demand_at_risk: 6.8 };
type KpiKey = keyof typeof SAMPLE;

/** A bracket over a stretch of the chart: |——— label ———|, positioned in chart (viewBox) units. */
function Span({ from, to, width, children, tone, title }: {
  from: number; to: number; width: number; children: React.ReactNode; tone?: string; title?: string;
}) {
  return (
    <div className={`span-bracket${tone ? ` ${tone}` : ""}`} title={title}
         style={{ left: `${(from / width) * 100}%`, width: `${((to - from) / width) * 100}%` }}>
      <span className="sb-line" /><span className="sb-label">{children}</span><span className="sb-line" />
    </div>
  );
}

/** "Jan–Oct" when the plan starts in November. */
function ytdMonths(forecastStart: string): string {
  const [y, mo] = forecastStart.split("-").map(Number);
  if (!y || !mo || mo === 1) return "no closed months";
  const last = fmtMonth(`${y}-${String(mo - 1).padStart(2, "0")}`).split(" ")[0];
  return mo === 2 ? last : `Jan–${last}`;
}

/** Unified compact top metrics bar with 3-year graphical line chart and horizontal KPI meters. */
export default function MetricsBar({ macro, refreshKey }: { macro: MacroScope; refreshKey: string }) {
  const [m, setM] = useState<Metrics | null>(null);
  const [error, setError] = useState("");
  const [hoverIdx, setHoverIdx] = useState<number | null>(null);
  const { family, group1, group2 } = macro;

  useEffect(() => {
    let live = true;
    setError("");
    api.metrics({ family, group1, group2 })
      .then((d) => { if (live) setM(d); })
      .catch((e) => { if (live) setError(e.message); });
    return () => { live = false; };
  }, [family, group1, group2, refreshKey]);

  const scope = [family, group1, group2].filter(Boolean).join(" · ") || "All products";

  // Build or format timeline data
  const timeline = useMemo(() => {
    if (!m) return [];
    if (m.timeline && m.timeline.length > 0) return m.timeline;
    // Fallback if timeline array was omitted: generate 34 points based on years & forecast start
    const curYear = m.current_year || 2026;
    const [, fsMo] = (m.forecast_start || `${curYear}-11`).split("-").map(Number);
    const [prevY, curY, nextY] = m.years;
    const pts = [];
    for (const yr of [curYear - 1, curYear, curYear + 1]) {
      const isCur = yr === curYear;
      const count = yr === curYear + 1 ? 10 : 12;
      const yrVal = yr === curYear - 1 ? prevY?.value || 0 : yr === curYear ? curY?.value || 0 : nextY?.value || 0;
      for (let mo = 1; mo <= count; mo++) {
        const mStr = `${yr}-${String(mo).padStart(2, "0")}`;
        const isAct = yr < curYear || (isCur && mo < fsMo);
        const val = isCur
          ? (isAct ? (m.ytd / (fsMo - 1 || 1)) : (m.ytg / (13 - fsMo || 1)))
          : yrVal / count;
        pts.push({ month: mStr, value: val, is_actual: isAct, year: yr });
      }
    }
    return pts;
  }, [m]);

  if (error) {
    return (
      <div className="canvas-overlay metrics-bar-box">
        <span className="small error-text" style={{ padding: "16px 20px" }}>Couldn't load metrics: {error}</span>
      </div>
    );
  }
  if (!m) {
    return (
      <div className="canvas-overlay metrics-bar-box" aria-busy>
        <div className="row muted small" style={{ padding: "16px 20px" }}>
          <div className="spinner" /> Reading from Kinaxis…
        </div>
      </div>
    );
  }

  const [prev, cur, next] = m.years;

  // SVG coordinate calculations (viewBox 0 0 1000 56)
  const W = 1000;
  const H = 56;
  const padX = 14;
  const topY = 6;
  const botY = 48;
  const iw = W - 2 * padX;
  const ih = botY - topY;

  const vals = timeline.map((p) => p.value);
  const minV = Math.min(...vals, 0);
  const maxV = Math.max(...vals, 1);
  const spanV = (maxV - minV) || 1;

  // A fixed calendar axis, Jan of last year to Dec of next year: every month has its own slot,
  // so months with no data show as a gap instead of squeezing the line together.
  const N = 36;
  const monthIdx = (ym: string) => {
    const [y, mo] = ym.split("-").map(Number);
    return (y - prev.year) * 12 + (mo - 1);
  };
  const xAt = (i: number) => padX + (i / (N - 1)) * iw;
  const coords = timeline
    .map((p) => ({ ...p, i: monthIdx(p.month) }))
    .filter((p) => p.i >= 0 && p.i < N)
    .sort((a, b) => a.i - b.i)
    .map((p) => ({ ...p, x: xAt(p.i), y: botY - ((p.value - minV) / spanV) * ih }));

  // Where the plan starts (the data's "now"); the marker sits on it.
  const nowIdx = m.forecast_start ? monthIdx(m.forecast_start.slice(0, 7)) : -1;
  const xNowMark = nowIdx >= 0 && nowIdx < N ? xAt(nowIdx) : null;
  const nowPoint = coords.find((p) => p.i === nowIdx) ?? null;
  const cutCoord = xNowMark != null ? { x: xNowMark, y: nowPoint ? nowPoint.y : botY } : null;

  // Lines and areas, broken wherever a month is missing; plan joins the last actual month.
  const runs = (pts: typeof coords) => {
    const out: (typeof coords)[] = [];
    for (const p of pts) {
      const last = out[out.length - 1];
      if (last && p.i === last[last.length - 1].i + 1) last.push(p);
      else out.push([p]);
    }
    return out;
  };
  const xy = (p: { x: number; y: number }) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`;
  const line = (rs: (typeof coords)[]) => rs.map((r) => "M " + r.map(xy).join(" L ")).join(" ");
  const area = (rs: (typeof coords)[]) => rs.filter((r) => r.length > 1).map((r) =>
    `M ${r[0].x.toFixed(1)},${botY + 4} L ${r.map(xy).join(" L ")} L ${r[r.length - 1].x.toFixed(1)},${botY + 4} Z`).join(" ");
  const actRuns = runs(coords.filter((p) => p.is_actual));
  const lastAct = coords.filter((p) => p.is_actual).slice(-1)[0];
  const planCoords = coords.filter((p) => !p.is_actual);
  const planRuns = runs(lastAct && planCoords[0]?.i === lastAct.i + 1 ? [lastAct, ...planCoords] : planCoords);
  const lineAct = line(actRuns), areaAct = area(actRuns);
  const linePlan = line(planRuns), areaPlan = area(planRuns);

  // Year boundaries (January of this year and next year)
  const xYr26 = xAt(12);
  const xYr27 = xAt(24);

  // Bracket edges are the lines the chart draws: the year dividers and the plan-start marker.
  const xStart = xAt(0);
  const xEnd = xAt(N - 1);
  const xCurStart = xYr26;
  const xNextStart = xYr27;
  const xNow = xNowMark ?? xCurStart;
  const seg = (from: number, to: number) => (to - from > 1 ? { from, to } : null);
  const yearSpans = [
    { y: prev, label: "Actuals", span: seg(xStart, xCurStart) },
    { y: cur, label: "Plan", span: seg(xCurStart, xNextStart) },
    { y: next, label: "Plan", span: seg(xNextStart, xEnd) },
  ];
  const ytdSpan = seg(xCurStart, Math.min(xNow, xNextStart));
  const ytgSpan = seg(Math.max(xNow, xCurStart), xNextStart);

  const kpi = (k: KpiKey) => (m[k] != null ? { v: m[k] as number, sample: false } : { v: SAMPLE[k], sample: true });
  const fill = kpi("fill_rate"), wape = kpi("wape"), bias = kpi("bias"), risk = kpi("demand_at_risk");
  const sampleNote = " · sample value: no source for this in Kinaxis yet";

  const hoveredPoint = hoverIdx != null ? coords.find((p) => p.i === hoverIdx) ?? null : null;
  const accuracy = m.accuracy_months.length
    ? `Statistical forecast vs actuals, ${monthRange(m.accuracy_months)}`
    : "No past Statistical forecast in Kinaxis to measure against";

  return (
    <div className="canvas-overlay metrics-bar-box" aria-label={`Key metrics for ${scope}`}>
      {/* ── Left: Scope & SKU count ──────────────────────────────── */}
      <div className="box-scope">
        <div className="scope-tag">
          <svg className="scope-icon" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
            <path d="M2.5 4a1.5 1.5 0 0 1 1.5-1.5h8A1.5 1.5 0 0 1 13.5 4v8a1.5 1.5 0 0 1-1.5 1.5H4A1.5 1.5 0 0 1 2.5 12V4zm2-.5a.5.5 0 0 0-.5.5v8a.5.5 0 0 0 .5.5h8a.5.5 0 0 0 .5-.5V4a.5.5 0 0 0-.5-.5H4.5z"/>
            <path d="M5 6.5A.5.5 0 0 1 5.5 6h5a.5.5 0 0 1 0 1h-5a.5.5 0 0 1-.5-.5zm0 3a.5.5 0 0 1 .5-.5h3a.5.5 0 0 1 0 1h-3a.5.5 0 0 1-.5-.5z"/>
          </svg>
          <span>Scope</span>
        </div>
        <div className="scope-title" title={scope}>{scope}</div>
        <span className="scope-badge">{m.scope.sku_count} SKUs</span>
      </div>

      {/* ── Center: 3-Year Line Chart & Monetary Stations ───────── */}
      <div className="box-chart-section">
        {/* Year totals, each over its own months */}
        <div className="span-row top">
          {yearSpans.map(({ y, label, span }) => span && (
            <Span key={y.year} from={span.from} to={span.to} width={W} tone={y.year === cur.year ? "cur" : undefined}
                  title={`${y.year} ${label.toLowerCase()}${y.year === cur.year ? " (actuals to date + plan)" : ""}: ${moneyDetail.format(y.value)}`}>
              <span className="sb-year">{y.year} {label}</span> <b>{money.format(y.value)}</b>
            </Span>
          ))}
        </div>

        {/* SVG Horizon Line Chart */}
        <div className="chart-canvas-wrap">
          <svg
            className="metrics-svg-chart"
            viewBox={`0 0 ${W} ${H}`}
            preserveAspectRatio="none"
            onPointerMove={(e) => {
              const rect = e.currentTarget.getBoundingClientRect();
              const vbX = ((e.clientX - rect.left) / rect.width) * W;
              const idx = Math.round(((vbX - padX) / iw) * (N - 1));
              setHoverIdx(Math.max(0, Math.min(N - 1, idx)));
            }}
            onPointerLeave={() => setHoverIdx(null)}
          >
            <defs>
              {/* Actuals area gradient */}
              <linearGradient id="act-grad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="var(--accent)" stopOpacity="0.32" />
                <stop offset="90%" stopColor="var(--accent)" stopOpacity="0.04" />
                <stop offset="100%" stopColor="var(--accent)" stopOpacity="0.0" />
              </linearGradient>
              {/* Plan area gradient */}
              <linearGradient id="plan-grad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="var(--fn-consensus)" stopOpacity="0.22" />
                <stop offset="90%" stopColor="var(--fn-consensus)" stopOpacity="0.03" />
                <stop offset="100%" stopColor="var(--fn-consensus)" stopOpacity="0.0" />
              </linearGradient>
            </defs>

            {/* Year boundary divider ticks */}
            {xYr26 && (
              <g className="boundary-marker">
                <line x1={xYr26} y1={topY} x2={xYr26} y2={botY + 4} stroke="var(--hairline)" strokeWidth="1" strokeDasharray="2 3" vectorEffect="non-scaling-stroke" />
              </g>
            )}
            {xYr27 && (
              <g className="boundary-marker">
                <line x1={xYr27} y1={topY} x2={xYr27} y2={botY + 4} stroke="var(--hairline)" strokeWidth="1" strokeDasharray="2 3" vectorEffect="non-scaling-stroke" />
              </g>
            )}

            {/* Shaded Areas */}
            {areaAct && <path d={areaAct} fill="url(#act-grad)" />}
            {areaPlan && <path d={areaPlan} fill="url(#plan-grad)" />}

            {/* Line Strokes */}
            {lineAct && <path d={lineAct} fill="none" stroke="var(--accent)" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />}
            {linePlan && <path d={linePlan} fill="none" stroke="var(--fn-consensus)" strokeWidth="2" strokeDasharray="4 3" strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />}

            {/* Current month cutover divider */}
            {cutCoord && (
              <g className="cutover-marker">
                <line x1={cutCoord.x} y1={topY - 2} x2={cutCoord.x} y2={botY + 6} stroke="var(--today-ink)" strokeWidth="1.5" strokeDasharray="3 2" vectorEffect="non-scaling-stroke" />
                <circle cx={cutCoord.x} cy={cutCoord.y} r="3.5" fill="var(--surface)" stroke="var(--today-ink)" strokeWidth="2" />
                <text x={cutCoord.x - 4} y={topY + 7} textAnchor="end" fontSize="9" fontWeight="600" fill="var(--today-ink)" fontFamily="sans-serif">{m.forecast_start ? `Plan from ${fmtMonth(m.forecast_start.slice(0, 7))}` : "Now"}</text>
              </g>
            )}

            {/* Hover Crosshair & Dot */}
            {hoveredPoint && (
              <g className="hover-marker">
                <line x1={hoveredPoint.x} y1={topY} x2={hoveredPoint.x} y2={botY + 4} stroke="var(--ink-muted)" strokeWidth="1" strokeDasharray="2 2" vectorEffect="non-scaling-stroke" />
                <circle cx={hoveredPoint.x} cy={hoveredPoint.y} r="4" fill="var(--surface)" stroke={hoveredPoint.is_actual ? "var(--accent)" : "var(--fn-consensus)"} strokeWidth="2.5" />
              </g>
            )}
          </svg>

          {/* Interactive Hover Tooltip */}
          {hoveredPoint && (
            <div
              className="chart-hover-pill"
              style={{
                left: `${(hoveredPoint.x / W) * 100}%`,
              }}
            >
              <span className="hp-month">{fmtMonth(hoveredPoint.month)}</span>
              <span className="hp-val">{moneyDetail.format(hoveredPoint.value)}</span>
              <span className={`hp-tag ${hoveredPoint.is_actual ? "act" : "plan"}`}>
                {hoveredPoint.is_actual ? "Actuals" : "Plan"}
              </span>
            </div>
          )}
        </div>

        {/* This year: actuals to date, then plan to go */}
        <div className="span-row bottom">
          {ytdSpan && (
            <Span from={ytdSpan.from} to={ytdSpan.to} width={W} tone="ytd"
                  title={`Actuals to date (${ytdMonths(m.forecast_start)}): ${moneyDetail.format(m.ytd)}`}>
              <b>{money.format(m.ytd)}</b>
            </Span>
          )}
          {ytgSpan && (
            <Span from={ytgSpan.from} to={ytgSpan.to} width={W} tone="ytg"
                  title={`Plan for the rest of ${cur.year}: ${moneyDetail.format(m.ytg)}`}>
              <b>{money.format(m.ytg)}</b>
            </Span>
          )}
        </div>
      </div>

      {/* ── Right: operational metrics, one line each, fitted to the chart's height ── */}
      <div className="box-kpis" aria-label="Operational health and risk metrics">
        <div className={`kpi-line${fill.sample ? " sample" : ""}`} title={`Fill rate: shipped ÷ actual demand, last 3 closed months${fill.sample ? sampleNote : ""}`}>
          <span className="kpi-bar-label">Fill rate</span>
          <div className="kpi-bar-track">
            <div className={`kpi-bar-fill ${fill.v >= 95 ? "good" : fill.v >= 85 ? "warn" : "bad"}`}
                 style={{ width: `${Math.min(100, Math.max(0, fill.v))}%` }} />
            <div className="target-pin" style={{ left: "95%" }} title="95% target" />
          </div>
          <span className="kpi-bar-val">{pct(fill.v)}</span>
        </div>
        <div className={`kpi-line${wape.sample ? " sample" : ""}`} title={`WAPE: ${accuracy}${wape.sample ? sampleNote : ""}`}>
          <span className="kpi-bar-label">WAPE</span>
          <div className="kpi-bar-track">
            <div className={`kpi-bar-fill ${wape.v <= 25 ? "good" : wape.v <= 40 ? "warn" : "bad"}`}
                 style={{ width: `${Math.min(100, (wape.v / 50) * 100)}%` }} />
          </div>
          <span className="kpi-bar-val">{pct(wape.v)}</span>
        </div>
        <div className={`kpi-line${bias.sample ? " sample" : ""}`}
             title={`Bias (${bias.v > 0 ? "over" : "under"}-forecast): ${accuracy}${bias.sample ? sampleNote : ""}`}>
          <span className="kpi-bar-label">Bias</span>
          <div className="kpi-bar-track bipolar">
            <div className="bipolar-zero" />
            <div className={`kpi-bar-fill ${Math.abs(bias.v) <= 5 ? "good" : Math.abs(bias.v) <= 15 ? "warn" : "bad"}`}
                 style={{
                   left: bias.v < 0 ? `${50 - Math.min(50, (Math.abs(bias.v) / 25) * 50)}%` : "50%",
                   width: `${Math.min(50, (Math.abs(bias.v) / 25) * 50)}%`,
                 }} />
          </div>
          <span className="kpi-bar-val">{pct(bias.v, true)}</span>
        </div>
        <div className={`kpi-line${risk.sample ? " sample" : ""}`}
             title={`Demand at risk: share of consensus that feasible supply doesn't cover${risk.sample ? sampleNote : ""}`}>
          <span className="kpi-bar-label">At risk</span>
          <div className="kpi-bar-track">
            <div className={`kpi-bar-fill ${risk.v > 0 ? "bad" : "good"}`}
                 style={{ width: `${Math.min(100, (risk.v / 25) * 100)}%` }} />
          </div>
          <span className={`kpi-bar-val${risk.v > 0 ? " risk" : ""}`}>{pct(risk.v)}</span>
        </div>
        {(fill.sample || wape.sample || bias.sample || risk.sample) && <span className="kpi-sample-note">dimmed = sample values</span>}
      </div>
    </div>
  );
}
